import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import LayoutWithChat from './LayoutWithChat';
import { 
  getPlatformStatuses, 
  initYahooAuth, 
  handleYahooCallback, 
  connectSleeper, 
  disconnectYahoo, 
  disconnectSleeper 
} from '../api/auth';

interface OnboardingPageProps {
  onComplete?: () => void;
  onSkip?: () => void;
}

const OnboardingPage: React.FC<OnboardingPageProps> = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const [sleeperUsername, setSleeperUsername] = useState('');
  const [showSleeperModal, setShowSleeperModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Connection states
  const [yahooConnected, setYahooConnected] = useState<boolean | null>(null);
  const [sleeperConnected, setSleeperConnected] = useState<boolean | null>(null);

  // Check platform statuses and OAuth callback on component mount
  useEffect(() => {
    checkPlatformStatuses();
    
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state && !isLoading) {
      // Clear the URL parameters immediately to prevent duplicate calls
      window.history.replaceState({}, document.title, window.location.pathname);
      handleOAuthCallback(code, state);
    }
  }, []);

  const checkPlatformStatuses = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const statuses = await getPlatformStatuses();
      setYahooConnected(statuses.yahoo.isOnboarded);
      setSleeperConnected(statuses.sleeper.isOnboarded);
      
      // If both platforms are connected, redirect to leagues page
      if (statuses.yahoo.isOnboarded && statuses.sleeper.isOnboarded) {
        navigate('/leagues');
        return;
      }
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to check connection status');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthCallback = async (code: string, state: string) => {
    setIsLoading(true);
    setError('');
    
    try {
      await handleYahooCallback(code, state);
      setYahooConnected(true);
      
      // Check if both platforms are now connected
      if (sleeperConnected) {
        navigate('/leagues');
        return;
      }
      
      if (onComplete) onComplete();
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        // Handle session expiry - redirect to login
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to complete Yahoo authentication. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleYahooConnect = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Use empty string for localhost, otherwise use current URL
      const redirectUri = window.location.hostname === 'localhost' 
        ? 'https://dev.fantasygenius.app/onboarding' 
        : window.location.origin + window.location.pathname;
      const response = await initYahooAuth(redirectUri);
      
      if (response.authUrl) {
        // Redirect to Yahoo OAuth
        window.location.href = response.authUrl;
      }
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        // Handle session expiry - redirect to login
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to connect to Yahoo Fantasy. Please try again.');
      }
      setIsLoading(false);
    }
  };

  const handleSleeperConnect = () => {
    setShowSleeperModal(true);
    setError(''); // Clear any previous errors
  };

  const handleSleeperImport = async () => {
    if (!sleeperUsername.trim()) {
      setError('Please enter your Sleeper username');
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await connectSleeper(sleeperUsername);
      setSleeperConnected(true);
      setShowSleeperModal(false);
      setSleeperUsername('');
      
      // Check if both platforms are now connected
      if (yahooConnected) {
        navigate('/leagues');
        return;
      }
      
      if (onComplete) onComplete();
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        // Handle session expiry - redirect to login
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError(err.message || 'Failed to connect Sleeper account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleYahooDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Yahoo Fantasy account?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await disconnectYahoo();
      setYahooConnected(false);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to disconnect Yahoo account');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSleeperDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Sleeper account?')) {
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      await disconnectSleeper();
      setSleeperConnected(false);
    } catch (err: any) {
      if (err.message === 'UNAUTHORIZED') {
        localStorage.removeItem('google_token');
        navigate('/');
      } else {
        setError('Failed to disconnect Sleeper account');
      }
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <LayoutWithChat>
        <div className="min-h-screen bg-bg-primary text-text-primary font-primary antialiased">
          <div className="flex flex-col items-center justify-start min-h-screen p-4 pt-16">
          {/* Main Content */}
          <main className="w-full max-w-2xl">
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-light font-primary text-text-primary mb-4">Import Your Leagues</h1>
              <p className="text-lg text-text-secondary font-secondary">
                Connect your fantasy accounts to get started.
              </p>
            </div>

            <div className="bg-bg-secondary border border-border-primary rounded-lg">
              <div className="p-6">
                <h2 className="text-2xl font-normal font-primary text-text-primary">
                  Connect Your Accounts
                </h2>
                <p className="mt-2 text-base font-light text-text-secondary font-secondary">
                  Choose a platform to import your fantasy football leagues.
                </p>
              </div>
              <div className="border-t border-border-primary p-6 space-y-4">
                {/* Yahoo Fantasy */}
                <div className="flex items-center justify-between p-4 bg-bg-tertiary/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img
                      alt="Yahoo Fantasy logo"
                      className="w-8 h-8"
                      src="https://s.yimg.com/cv/apiv2/default/icons/favicon_y19_32x32_custom.svg"
                    />
                    <div>
                      <h3 className="text-base font-medium text-text-primary font-primary">
                        Yahoo Fantasy
                      </h3>
                      <p className="text-sm font-light text-text-secondary font-secondary">
                        {yahooConnected === null 
                          ? 'Checking...' 
                          : yahooConnected 
                            ? 'Connected' 
                            : 'Use OAuth to securely connect your account.'}
                      </p>
                    </div>
                  </div>
                  {yahooConnected === null ? (
                    <div className="w-20 h-8 bg-bg-tertiary animate-pulse rounded-lg"></div>
                  ) : yahooConnected ? (
                    <button
                      onClick={handleYahooDisconnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  ) : (
                    <button
                      onClick={handleYahooConnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>

                {/* Sleeper */}
                <div className="flex items-center justify-between p-4 bg-bg-tertiary/50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <img
                      alt="Sleeper app logo"
                      className="w-8 h-8 rounded-full"
                      src="https://cdn.brandfetch.io/idMCm9AmqM/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1747579480249"
                    />
                    <div>
                      <h3 className="text-base font-medium text-text-primary font-primary">
                        Sleeper
                      </h3>
                      <p className="text-sm font-light text-text-secondary font-secondary">
                        {sleeperConnected === null 
                          ? 'Checking...' 
                          : sleeperConnected 
                            ? 'Connected' 
                            : 'Enter your username to import your leagues.'}
                      </p>
                    </div>
                  </div>
                  {sleeperConnected === null ? (
                    <div className="w-20 h-8 bg-bg-tertiary animate-pulse rounded-lg"></div>
                  ) : sleeperConnected ? (
                    <button
                      onClick={handleSleeperDisconnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-red-400 hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {isLoading ? 'Disconnecting...' : 'Disconnect'}
                    </button>
                  ) : (
                    <button
                      onClick={handleSleeperConnect}
                      disabled={isLoading}
                      className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Skip button */}
            <div className="mt-8 flex justify-center">
              <button
                onClick={() => {
                  if (onSkip) onSkip();
                  navigate('/leagues');
                }}
                className="text-text-secondary hover:text-text-primary font-medium font-secondary transition-colors"
              >
                I'll do this later
              </button>
            </div>
          </main>
          </div>
        </div>
      </LayoutWithChat>

      {/* Sleeper Username Modal */}
      <Modal 
        isOpen={showSleeperModal} 
        onClose={() => setShowSleeperModal(false)}
        title="Connect Sleeper"
      >
        <div className="space-y-4">
          <p className="text-text-secondary font-secondary">
            Enter your Sleeper username to import your leagues.
          </p>
          
          <input
            className="w-full bg-bg-tertiary border border-border-primary rounded-lg py-3 px-4 
                     focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent 
                     transition text-text-primary placeholder-text-secondary font-secondary"
            placeholder="Enter your Sleeper username"
            type="text"
            value={sleeperUsername}
            onChange={(e) => setSleeperUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSleeperImport();
              }
            }}
            autoFocus
          />
          
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowSleeperModal(false)}
              className="text-text-secondary hover:text-text-primary font-medium font-secondary transition-colors px-4 py-2"
            >
              Cancel
            </button>
            <button
              onClick={handleSleeperImport}
              disabled={!sleeperUsername.trim() || isLoading}
              className="bg-bg-tertiary text-text-secondary hover:bg-white/10 hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {isLoading ? 'Connecting...' : 'Import Leagues'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default OnboardingPage;